// @ts-nocheck
import { OPTION_TYPE_COUPLING_CLIP } from '../constants';
import { IAssistantMessage, IQuestion, IQuestionOption } from '../types';

const ANGLE_KEY_RE = /^angle_\d+_\(°\)$/i;

/** Extract all submitted angle values (degrees > 0) from a bay window dimension form. */
export const extractAnglesFromFormValues = (formValues: Record<string, unknown>): number[] =>
  Object.entries(formValues)
    .filter(([key]) => ANGLE_KEY_RE.test(key))
    .map(([, v]) => Number(v))
    .filter(v => !isNaN(v) && v > 0);

/**
 * Calculate bay poles and coupling clips from entered angle values.
 *
 * Rules (from pre-UAT demo + Workshop 8):
 *   - Each non-180° angle junction  → 1 bay pole
 *   - Each 180° angle junction       → 1 coupling clip (flat join, no pole)
 *   - Angled sill horns = Yes        → +2 bay poles on top
 *
 * Returns `hasAngles: false` when no angle fields are present in the form so
 * callers can fall back to the existing default rather than overriding with 0.
 */
export const calculateBayPolesAndCouplingClips = (
  formValues: Record<string, unknown>,
  angledSillHornsYes: boolean,
): { bayPoles: number; couplingClips: number; hasAngles: boolean } => {
  const angles = extractAnglesFromFormValues(formValues);
  if (angles.length === 0) return { bayPoles: 0, couplingClips: 0, hasAngles: false };

  const couplingClips = angles.filter(a => a === 180).length;
  const bayPoles = angles.filter(a => a !== 180).length + (angledSillHornsYes ? 2 : 0);
  return { bayPoles, couplingClips, hasAngles: true };
};

export const COUPLING_CLIP_MSG_ID = 'coupling_clip_auto_message';
export const DIMENSION_ALERT_ID = 'dimension_config_alert';
export const SPRAY_COLOUR_ALERT_ID = 'spray_colour_different_alert';

/**
 * Build a synthetic user message so coupling clips appear in the price breakdown.
 * The message's form stores the clip count under the option's responseValue so
 * `getUnitMultiplier` in configurationPriceCalculation picks it up as the quantity.
 * Returns null when no priced coupling-clip question is found in the flow.
 */
export const buildCouplingClipMessage = (
  couplingClips: number,
  questionFlow: IQuestion[],
): IAssistantMessage | null => {
  if (couplingClips <= 0) return null;

  const clipQ = questionFlow.find(q => q.optionType === OPTION_TYPE_COUPLING_CLIP);
  if (!clipQ?.options?.length) return null;

  // Prefer first option that has a non-zero price; fall back to any option.
  const clipOption: IQuestionOption =
    clipQ.options.find(o => (o.priceModifier ?? o.price ?? 0) > 0) ?? clipQ.options[0];
  if (!clipOption) return null;

  // The form key must match option.responseValue so getUnitMultiplier resolves
  // the entered quantity from the form and scales the price correctly.
  const formKey = clipOption.responseValue || clipOption.optionId;

  return {
    id: COUPLING_CLIP_MSG_ID,
    type: 'user',
    message: `Coupling Clips: ${couplingClips}`,
    question: clipQ,
    response: {
      answer: [clipOption],
      selectedOptionsIds: [clipOption.optionId],
      form: { [formKey]: couplingClips },
    },
  };
};
